import { useCallback, useEffect, useMemo, useState } from "react";

import { setMenuItemStopList } from "services/menu/rest";
import {
    MenuCategoryDto,
    MenuItemListDto,
    MenuItemsListFilterDto,
} from "services/menu/restModels";

import { useConstants } from "shared/hooks/useConstants";
import { useCurrencyId } from "shared/hooks/useCurrencyId";
import { useDebouncedValue } from "shared/hooks/useDebouncedValue";
import { useLocale } from "shared/hooks/useLocale";
import { useMenuCategories } from "shared/hooks/useMenuCategories";
import { usePageNotification } from "shared/hooks/usePageNotification";
import { useMenuScope } from "shared/context/MenuScopeContext";
import { CurrencyFormatter, createCurrencyFormatter } from "shared/lib/currencyFormatter";
import { matchesLocalizedQuery } from "shared/lib/localizedMap";
import { useNotificationSnackbar, UseNotificationSnackbarReturn } from "shared/ui";

import { SEARCH_DEBOUNCE_MS } from "features/MenuEditor/constants";
import {
    MenuItemsListState,
    useMenuItemsList,
} from "features/MenuEditor/hooks/useMenuItemsList";
import {
    MenuEditorMutations,
    useMenuEditorMutations,
} from "features/MenuEditor/hooks/useMenuEditorMutations";
import { MenuEditorDialog } from "features/MenuEditor/types";

export interface MenuEditorState {
    menuId: number;
    locale: string;
    currencyId: string;
    currencyFormatter: CurrencyFormatter;
    currencyShortName: string;
    formatPrice: (price: number) => string;

    categories: MenuCategoryDto[];
    activeCategory: MenuCategoryDto | null;
    selectCategory: (menuCategoryId: number) => void;
    categoriesQuery: string;
    setCategoriesQuery: (value: string) => void;
    visibleCategories: MenuCategoryDto[];

    itemsQuery: string;
    setItemsQuery: (value: string) => void;
    // true, пока в поиске есть запрос: заголовок категории показывает найденное количество.
    hasSearchQuery: boolean;
    filter: MenuItemsListFilterDto;
    setFilter: (filter: MenuItemsListFilterDto) => void;

    list: MenuItemsListState;
    toggleStopList: (item: MenuItemListDto) => void;
    togglingItemId: number | null;

    dialog: MenuEditorDialog;
    openDialog: (dialog: MenuEditorDialog) => void;
    closeDialog: () => void;
    mutations: MenuEditorMutations;
    // Полное обновление экрана после массовой операции (загрузка позиций из файла).
    refreshAll: () => void;

    summary: { categoriesCount: number; itemsCount: number; stopListItemsCount: number };
    loading: boolean;
    error: boolean;

    notify: UseNotificationSnackbarReturn;
}

export const useMenuEditorState = (): MenuEditorState => {
    const { menuId } = useMenuScope();
    const locale = useLocale();
    const currencyId = useCurrencyId();
    const { constants } = useConstants();
    const notify = useNotificationSnackbar();

    // Сюда приезжают сообщения от редактора позиции: он уводит на этот экран после сохранения
    // и удаления, и показать их можно только здесь.
    usePageNotification(notify.showSuccess);

    const currency = constants?.currencies.find((item) => item.id === currencyId) || null;

    const {
        categories,
        summary,
        loading: categoriesLoading,
        error: categoriesError,
        refetch: refreshCategories,
        adjustStopListCount: adjustCategoryStopListCount,
    } = useMenuCategories(menuId);

    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
    const [categoriesQuery, setCategoriesQuery] = useState<string>("");
    const [itemsQuery, setItemsQuery] = useState<string>("");
    const [filter, setFilter] = useState<MenuItemsListFilterDto>("all");
    const [togglingItemId, setTogglingItemId] = useState<number | null>(null);
    const [dialog, setDialog] = useState<MenuEditorDialog>({ kind: "none" });

    const debouncedItemsQuery = useDebouncedValue(itemsQuery, SEARCH_DEBOUNCE_MS);
    // Очистка поиска применяется сразу, без дебаунса: иначе после сброса запроса экран
    // на 300 мс оставался бы в режиме поиска и успевал сходить за списком дважды.
    const effectiveQuery = itemsQuery.trim() === "" ? "" : debouncedItemsQuery.trim();
    const hasSearchQuery = effectiveQuery.length > 0;

    // Первая категория выбирается сама; если выбранную удалили — падаем на первую живую.
    useEffect(() => {
        if (categories.length === 0) {
            setSelectedCategoryId(null);
            return;
        }

        setSelectedCategoryId((current) =>
            current !== null && categories.some((item) => item.id === current)
                ? current
                : categories[0].id
        );
    }, [categories]);

    const activeCategory = useMemo(
        () => categories.find((item) => item.id === selectedCategoryId) || null,
        [categories, selectedCategoryId]
    );

    // Поиск идёт внутри выбранной категории: иначе выбранная слева категория и выдача
    // справа расходятся, и непонятно, к чему относится список.
    const list = useMenuItemsList({
        menuId,
        menuCategoryId: selectedCategoryId,
        query: effectiveQuery,
        filter,
    });

    const {
        patchItem,
        reload: reloadItems,
        removeItem: removeItemFromList,
        adjustStopListCount: adjustListStopListCount,
    } = list;

    const openDialog = useCallback((next: MenuEditorDialog) => setDialog(next), []);
    const closeDialog = useCallback(() => setDialog({ kind: "none" }), []);

    const mutations = useMenuEditorMutations({
        menuId,
        notify,
        refreshCategories,
        reloadItems,
        removeItemFromList,
    });

    const refreshAll = useCallback(() => {
        refreshCategories();
        reloadItems();
    }, [refreshCategories, reloadItems]);

    const visibleCategories = useMemo(
        () => categories.filter((item) => matchesLocalizedQuery(item.name, categoriesQuery)),
        [categories, categoriesQuery]
    );

    const currencyFormatter = useMemo(
        () => createCurrencyFormatter(currency, locale),
        [currency, locale]
    );
    const formatPrice = useCallback(
        (price: number) => currencyFormatter.toShow(price),
        [currencyFormatter]
    );

    // Поиск и фильтр при смене категории не сбрасываем: они теперь работают внутри
    // категории, и «то же условие в соседнем разделе» — обычный сценарий.
    const selectCategory = useCallback((menuCategoryId: number) => {
        setSelectedCategoryId(menuCategoryId);
    }, []);

    const toggleStopList = useCallback(
        (item: MenuItemListDto) => {
            setTogglingItemId(item.id);

            setMenuItemStopList(item.id, { isInStopList: !item.isInStopList })
                .then((updated) => {
                    if (!updated) return;

                    patchItem(updated);

                    // Счётчики стоп-листа (строка сайдбара и чип фильтра) правим на месте:
                    // перезапрос категорий поднял бы общий флаг загрузки и погасил экран.
                    const delta = updated.isInStopList ? 1 : -1;
                    adjustCategoryStopListCount(updated.menuCategoryId, delta);
                    adjustListStopListCount(delta);
                })
                .catch((error: Error) => {
                    notify.showError("Не удалось изменить доступность позиции", error);
                })
                .finally(() => setTogglingItemId(null));
        },
        // Зависим от конкретных функций, а не от объекта списка целиком: тот пересоздаётся
        // на каждый рендер и заставлял бы перерисовываться все плитки.
        [patchItem, adjustListStopListCount, adjustCategoryStopListCount, notify]
    );

    return {
        menuId,
        locale,
        currencyId,
        currencyFormatter,
        currencyShortName: currency?.shortName[locale] || "",
        formatPrice,

        categories,
        activeCategory,
        selectCategory,
        categoriesQuery,
        setCategoriesQuery,
        visibleCategories,

        itemsQuery,
        setItemsQuery,
        hasSearchQuery,
        filter,
        setFilter,

        list,
        toggleStopList,
        togglingItemId,

        dialog,
        openDialog,
        closeDialog,
        mutations,
        refreshAll,

        summary,
        loading: categoriesLoading,
        error: categoriesError,

        notify,
    };
};
